/**
 * OSM water features (issue #25): polygon waterbodies (loughs / ponds /
 * reservoirs) as flat translucent surfaces, and line waterways (the Rede,
 * burns) as thin terrain-following ribbons. Mirrors BuildingsLayer /
 * BeaconsLayer — pure three.js-free helpers unit-tested in Node, wrapped in a
 * three.js Group exposing update(sampleGround) so water drapes as terrain tiles
 * stream in.
 *
 * Purely cosmetic scene furniture: water has NO bearing on any LOS result (hard
 * constraint #1, guarded by water.guard.test.ts) and no elevation is invented —
 * every surface comes from the same collider sampleGround that backs walk-mode
 * ground lock (hard constraint #6).
 *
 * Water level: LIDAR over water is noisy, so a waterbody's flat surface is
 * placed at the MINIMUM sampled ground height along its ring (the banks are the
 * highest points, so the min is a stable proxy for the water surface) — a flat
 * cap that sits cleanly in the channel rather than following the noisy DTM.
 *
 * Geometry is MERGED per category (all bodies -> one mesh, all ways -> one
 * mesh) to keep draw calls low (issue #17). Geometry stays once built (never
 * dropped when a tile later unloads) — like the beacons' persistent grounding.
 *
 * Perf contract (fix for the 2 s animate/freeze cycle, same as roadWalls.ts):
 * a vertex is raycast only while unresolved — resolved heights are cached
 * forever; update() takes a per-call sample budget and reports whether work
 * remains; waterways build the resolved segments instead of waiting for every
 * vertex. Waterbodies still need their whole ring before capping (the flat
 * level is the min over the ring), but their sampling is cached like
 * everything else. Corridor rule (user guidance): lakes read as water at any
 * distance and are always kept; streams are near-route visuals only and are
 * clipped to the corridor; rivers are kept everywhere (the Rede is a main
 * visual feature and hugs the route anyway).
 *
 * Ripples + shoreline (issue #65): a single shared, tier-gated material
 * (waterRipple.ts) adds an animated ripple normal and a feathered/darkened
 * shoreline. Every vertex carries two extra build-time attributes so the
 * shader never needs a depth-buffer pass or per-frame raycast: `aEdge` (1 at
 * the bank, 0 in deep water — a signed lateral param for ribbons, a real
 * distance-to-bank feather for waterbody caps) and `aFlow` (per-vertex
 * advection vector; zero for lakes, which drift isotropically in the shader
 * instead). The only per-frame cost this adds is WaterLayer.tick(dt)
 * advancing one time uniform.
 */
import { BufferAttribute, BufferGeometry, Group, Mesh, MeshStandardMaterial, Shape, ShapeGeometry, Vector2 } from 'three';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { WaterFile } from '../core/manifest';
import type { GroundSampler } from '../camera/groundLock';
import { clipToCorridor } from './corridor';
import {
  FEATHER_DIST_M,
  RIVER_FLOW_SPEED_MPS,
  STREAM_FLOW_SPEED_MPS,
  createWaterMaterial,
  insetRing,
  setWaterMaterialTime,
} from './waterRipple';

/** Small lift above the sampled ground so a draped ribbon / cap doesn't z-fight the terrain. */
export const WATER_LIFT_M = 0.15;
export const RIVER_HALF_WIDTH_M = 3;
export const STREAM_HALF_WIDTH_M = 1;
/** Streams beyond this distance of the route corridor are dropped — a 2 m
 * burn is a near-route visual only. Lakes and rivers are kept everywhere. */
export const STREAM_CORRIDOR_M = 500;

const WATER_COLOR = 0x3b6ea5;

export function waterbodyLocalRing(polygon: [number, number][]): LocalXZ[] {
  return polygon.map(([e, n]) => bngToLocal(e, n));
}

export function waterwayLocalLine(polyline: [number, number][]): LocalXZ[] {
  return polyline.map(([e, n]) => bngToLocal(e, n));
}

/** Flat water level for a polygon: the minimum sampled ground height along its
 * ring (see module note). */
export function flatWaterLevel(groundHeights: number[]): number {
  return Math.min(...groundHeights);
}

/** Append a flat cap (triangulated in the x/z plane, held at `level`) into
 * the shared position/index/edge/flow arrays, every vertex tagged with a
 * fixed `edgeValue` for the shoreline feather (see appendPolygon). Lakes
 * never carry a baked-in flow direction (isotropic drift is a shader-side
 * constant, see waterRipple.ts), so flow is always (0, 0) here. */
function appendFlatCap(
  ring: LocalXZ[],
  level: number,
  edgeValue: number,
  positions: number[],
  indices: number[],
  edges: number[],
  flows: number[],
): void {
  if (ring.length < 3) return;
  const shape = new Shape(ring.map((p) => new Vector2(p.x, p.z)));
  const geo = new ShapeGeometry(shape);
  const pos = geo.getAttribute('position');
  const base = positions.length / 3;
  for (let i = 0; i < pos.count; i++) {
    // ShapeGeometry lays the shape in XY (z=0); remap to (x, level, z).
    positions.push(pos.getX(i), level, pos.getY(i));
    edges.push(edgeValue);
    flows.push(0, 0);
  }
  const idx = geo.getIndex();
  if (idx) for (let i = 0; i < idx.count; i++) indices.push(base + idx.getX(i));
  geo.dispose();
}

/**
 * Append a waterbody cap with a feathered shoreline (issue #65): a border
 * band between the original ring (edge=1, bank) and an inward-offset ring
 * (edge=0, deep water — see insetRing), plus a fully "deep" cap over the
 * inset polygon's interior. Both the band and the interior come from the
 * ring geometry alone, computed once at build time — no depth-buffer pass.
 *
 * Too small/oddly-shaped for a safe inset (insetRing returns null, e.g. a
 * pond narrower than twice the feather distance): falls back to a single
 * flat, unfeathered cap (edge=0 everywhere) rather than fabricating a band
 * that would fold over itself, or feathering the whole surface to
 * invisible.
 */
function appendPolygon(
  ring: LocalXZ[],
  level: number,
  positions: number[],
  indices: number[],
  edges: number[],
  flows: number[],
): void {
  if (ring.length < 3) return;
  const inset = insetRing(ring, FEATHER_DIST_M);
  if (!inset) {
    appendFlatCap(ring, level, 0, positions, indices, edges, flows);
    return;
  }

  const n = ring.length;
  const base = positions.length / 3;
  for (let i = 0; i < n; i++) {
    positions.push(ring[i].x, level, ring[i].z);
    edges.push(1);
    flows.push(0, 0);
  }
  for (let i = 0; i < n; i++) {
    positions.push(inset[i].x, level, inset[i].z);
    edges.push(0);
    flows.push(0, 0);
  }
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const a = base + i;
    const b = base + j;
    const c = base + n + i;
    const d = base + n + j;
    indices.push(a, c, b, b, c, d);
  }

  appendFlatCap(inset, level, 0, positions, indices, edges, flows);
}

/** Append a draped ribbon strip (per-vertex ground height, fixed half-width)
 * into the shared position/index/edge/flow arrays. Vertices whose ground has
 * not resolved yet are skipped: only segments between consecutive resolved
 * vertices are built, so a waterway crossing the terrain edge still gets the
 * stretch that CAN exist (nothing is fabricated over missing ground).
 *
 * Shoreline (issue #65): a ribbon has no interior vertices (both rails ARE
 * the bank), so distance-to-bank is meaningless here — instead each rail
 * gets a signed lateral parameter (-1 / +1) whose absolute value the shader
 * takes AFTER interpolation, so it reads as 1 at both banks and 0 exactly at
 * the (interpolated) centreline. Every vertex also gets a flow vector: the
 * local tangent direction scaled by flowSpeed, feeding the ripple advection.
 */
function appendRibbon(
  line: LocalXZ[],
  groundY: Array<number | null>,
  halfWidth: number,
  flowSpeed: number,
  positions: number[],
  indices: number[],
  edges: number[],
  flows: number[],
): void {
  const n = line.length;
  if (n < 2) return;
  const pairStart: number[] = new Array(n).fill(-1);
  for (let i = 0; i < n; i++) {
    const g = groundY[i];
    if (g === null) continue;
    const prev = line[Math.max(0, i - 1)];
    const next = line[Math.min(n - 1, i + 1)];
    let tx = next.x - prev.x;
    let tz = next.z - prev.z;
    const len = Math.hypot(tx, tz) || 1;
    tx /= len;
    tz /= len;
    const px = -tz; // horizontal perpendicular
    const pz = tx;
    const y = g + WATER_LIFT_M;
    pairStart[i] = positions.length / 3;
    positions.push(line[i].x + px * halfWidth, y, line[i].z + pz * halfWidth);
    positions.push(line[i].x - px * halfWidth, y, line[i].z - pz * halfWidth);
    edges.push(-1, 1);
    flows.push(tx * flowSpeed, tz * flowSpeed, tx * flowSpeed, tz * flowSpeed);
  }
  for (let i = 0; i < n - 1; i++) {
    if (pairStart[i] < 0 || pairStart[i + 1] < 0) continue; // unresolved end
    const a = pairStart[i];
    const b = pairStart[i] + 1;
    const c = pairStart[i + 1];
    const d = pairStart[i + 1] + 1;
    indices.push(a, c, b, b, c, d);
  }
}

function buildMesh(
  positions: number[],
  indices: number[],
  edges: number[],
  flows: number[],
  material: MeshStandardMaterial,
): Mesh | undefined {
  if (positions.length === 0) return undefined;
  const geo = new BufferGeometry();
  geo.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geo.setAttribute('aEdge', new BufferAttribute(new Float32Array(edges), 1));
  geo.setAttribute('aFlow', new BufferAttribute(new Float32Array(flows), 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const mesh = new Mesh(geo, material);
  mesh.renderOrder = 1; // draw transparent water after opaque terrain
  return mesh;
}

interface BodyState {
  ring: LocalXZ[];
  /** Cached ground height per ring vertex; null = not resolved yet. */
  groundY: Array<number | null>;
  /** Tile generation at which each still-null vertex was last attempted —
   * a vertex is asked at most once per tile set (NaN = never attempted). */
  attempt: number[];
  pending: number;
  /** Flat cap level, set once every ring vertex has resolved. */
  level: number | null;
}
interface WayState {
  line: LocalXZ[];
  halfWidth: number;
  /** Ripple advection speed for this waterway (issue #65: rivers flow faster
   * than streams). */
  flowSpeed: number;
  /** Cached ground height per vertex; null = not resolved yet. */
  groundY: Array<number | null>;
  /** See BodyState.attempt. */
  attempt: number[];
  pending: number;
}

export class WaterLayer {
  readonly group = new Group();
  private readonly material: MeshStandardMaterial;
  private readonly bodies: BodyState[] = [];
  private readonly ways: WayState[] = [];
  private bodyMesh?: Mesh;
  private wayMesh?: Mesh;
  /** Synthetic generations (negative, so they never collide with real tile
   * generations) handed out when update() is called without one. */
  private fallbackGeneration = 0;
  /** Elapsed time (s) driving the shared material's ripple uniform (see tick). */
  private elapsedS = 0;

  constructor(
    water: WaterFile,
    options: {
      includeStreams?: boolean;
      /** Route polyline (local coords); streams beyond streamCorridorMaxDistM
       * of it are dropped. Lakes and rivers are unaffected. */
      corridor?: LocalXZ[];
      streamCorridorMaxDistM?: number;
      /** High quality tier: ripple normal samples a texture. Low tier keeps
       * the same animation but falls back to analytic (vertex/fragment
       * math only) ripples, dropping the texture fetch (issue #17). */
      rippleTexture?: boolean;
    } = {},
  ) {
    this.group.name = 'water';
    const includeStreams = options.includeStreams ?? true;
    const streamMaxDistM = options.streamCorridorMaxDistM ?? STREAM_CORRIDOR_M;
    this.material = createWaterMaterial({
      rippleTexture: options.rippleTexture ?? true,
      color: WATER_COLOR,
      opacity: 0.62,
    });

    for (const b of water.waterbodies) {
      const ring = waterbodyLocalRing(b.polygon_bng);
      this.bodies.push({
        ring,
        groundY: new Array<number | null>(ring.length).fill(null),
        attempt: new Array<number>(ring.length).fill(Number.NaN),
        pending: ring.length,
        level: null,
      });
    }
    for (const w of water.waterways) {
      if (!includeStreams && w.kind === 'stream') continue;
      const line = waterwayLocalLine(w.polyline_bng);
      const runs =
        w.kind === 'stream' && options.corridor ? clipToCorridor(line, options.corridor, streamMaxDistM) : [line];
      for (const run of runs) {
        this.ways.push({
          line: run,
          halfWidth: w.kind === 'river' ? RIVER_HALF_WIDTH_M : STREAM_HALF_WIDTH_M,
          flowSpeed: w.kind === 'river' ? RIVER_FLOW_SPEED_MPS : STREAM_FLOW_SPEED_MPS,
          groundY: new Array<number | null>(run.length).fill(null),
          attempt: new Array<number>(run.length).fill(Number.NaN),
          pending: run.length,
        });
      }
    }
  }

  /** Advances the shared ripple time uniform (the only per-frame cost this
   * feature adds — no ground sampling, no geometry rebuild). */
  tick(dtS: number): void {
    this.elapsedS += dtS;
    setWaterMaterialTime(this.material, this.elapsedS);
  }

  /**
   * Resolves ground heights for vertices that don't have one yet, up to
   * maxSamples raycasts this call, and rebuilds the merged meshes if anything
   * newly resolved. A feature is never given a fabricated height; resolved
   * heights are cached forever, so once built a feature stays even if its
   * tile later unloads (persistent, like the beacons).
   *
   * Returns true if the sample budget ran out before every pending vertex was
   * attempted — i.e. "call me again, there may be more ground to find now".
   *
   * `generation` identifies the current terrain tile set (see
   * TerrainCollider.generation): a still-null vertex is attempted at most once
   * per generation, so a lake hanging off the map edge costs nothing once the
   * whole layer has been scanned for the current tiles. Omitting it retries
   * every still-null vertex (each call behaves as a fresh generation).
   */
  update(sampleGround: GroundSampler, maxSamples = Infinity, generation?: number): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    let samples = 0;
    let changed = false;
    let exhausted = false;

    outer: for (const feats of [this.bodies, this.ways] as const) {
      for (const f of feats) {
        if (f.pending === 0) continue;
        const pts = 'ring' in f ? f.ring : f.line;
        for (let i = 0; i < pts.length; i++) {
          if (f.groundY[i] !== null || f.attempt[i] === gen) continue;
          if (samples >= maxSamples) {
            exhausted = true;
            break outer;
          }
          samples += 1;
          f.attempt[i] = gen;
          const y = sampleGround(pts[i].x, pts[i].z);
          if (y !== null) {
            f.groundY[i] = y;
            f.pending -= 1;
            changed = true;
          }
        }
        if (f.pending === 0 && 'ring' in f) {
          f.level = flatWaterLevel(f.groundY as number[]);
        }
      }
    }

    if (changed) this.rebuild();
    return exhausted;
  }

  private rebuild(): void {
    const bodyPos: number[] = [];
    const bodyIdx: number[] = [];
    const bodyEdge: number[] = [];
    const bodyFlow: number[] = [];
    for (const b of this.bodies) {
      if (b.level !== null) appendPolygon(b.ring, b.level, bodyPos, bodyIdx, bodyEdge, bodyFlow);
    }

    const wayPos: number[] = [];
    const wayIdx: number[] = [];
    const wayEdge: number[] = [];
    const wayFlow: number[] = [];
    for (const w of this.ways) {
      if (w.pending < w.line.length) {
        appendRibbon(w.line, w.groundY, w.halfWidth, w.flowSpeed, wayPos, wayIdx, wayEdge, wayFlow);
      }
    }

    if (this.bodyMesh) {
      this.group.remove(this.bodyMesh);
      this.bodyMesh.geometry.dispose();
      this.bodyMesh = undefined;
    }
    if (this.wayMesh) {
      this.group.remove(this.wayMesh);
      this.wayMesh.geometry.dispose();
      this.wayMesh = undefined;
    }
    this.bodyMesh = buildMesh(bodyPos, bodyIdx, bodyEdge, bodyFlow, this.material);
    if (this.bodyMesh) this.group.add(this.bodyMesh);
    this.wayMesh = buildMesh(wayPos, wayIdx, wayEdge, wayFlow, this.material);
    if (this.wayMesh) this.group.add(this.wayMesh);
  }
}
